import { ShareSheet } from '@/features/sharing/ShareSheet'
import { Outlet } from 'react-router'
import { AutoplayCoordinator } from '@/audio/AutoplayCoordinator'

import { cn } from '@/lib/cn'

import { useJobStream } from '@/api/useJobStream'
import { usePendingPlayback } from '@/audio/pendingPlayback'
import { usePlayer } from '@/audio/store'
import { useIncognito } from '@/lib/incognito'
import { useKeyboardOpen } from '@/lib/keyboard'
import { Toaster } from '@/components/ui'
import { PlayerBar } from '@/features/player/PlayerBar'
import { ReleaseAnnouncement } from '@/features/releases/ReleaseAnnouncement'
import { DaylistPreparation } from '@/features/daylists/DaylistPreparation'

import { IncognitoBanner } from './IncognitoBanner'
import { Sidebar } from './Sidebar'
import { TabBar } from './TabBar'
import { TopBar } from './TopBar'

/**
 * The layout route.
 *
 * Everything that must survive navigation lives here: the nav, the docked
 * player, the toast region, and the job event stream. Only `<Outlet/>` swaps
 * when the route changes — the player is never unmounted, which is the whole
 * reason this shell exists.
 */
export function AppShell() {
  // One stream for the whole app. Pages must not open a second EventSource;
  // useJobs() reads the same query cache this keeps warm.
  //
  // The handler is what makes "press play on something you don't have yet"
  // actually end in playback: the intent is recorded when the download starts and
  // redeemed here when it finishes, wherever the user has navigated to since.
  useJobStream(true, usePendingPlayback.getState().handleJobEvent)

  // The same condition PlayerBar returns null on, kept as a boolean so this
  // only re-renders the shell when the bar appears or disappears — not on
  // every position tick.
  const playerDocked = usePlayer((state) => state.index >= 0 && state.index < state.queue.length)
  const incognito = useIncognito((state) => state.active)
  // iOS lifts fixed elements above the on-screen keyboard rather than leaving
  // them under it, so the docked bar ends up floating across the middle of the
  // screen while you type. Nobody needs a tab bar mid-search; it goes away.
  const keyboardOpen = useKeyboardOpen()

  return (
    <div className="min-h-dvh md:flex">
      <Sidebar />

      <main className="min-w-0 flex-1">
        <TopBar />
        <DaylistPreparation />
        <Outlet />

        {/* Reserve the space the docked bars occupy, so the last row of any list
            is scrollable clear of them. Separate elements rather than one calc()
            so the tab bar's share disappears cleanly at md:.

            The player's share is reserved only when the player is actually
            there. PlayerBar renders nothing until something is queued, so a
            fixed spacer left 4.5rem of empty document below the last row —
            which is mostly what "the scrollbar doesn't match the content" was:
            the page scrolled further than it had anything to show, and the
            thumb was sized for a document taller than the page. */}
        {playerDocked ? <div aria-hidden="true" className="h-player" /> : null}
        {/* The incognito banner shares the docked stack, so it has to be
            reserved too — otherwise it covers the last row of every list, which
            is the exact bug the conditional player spacer above exists to fix. */}
        {incognito ? <div aria-hidden="true" className="h-banner" /> : null}
        <div aria-hidden="true" className="h-tabbar md:hidden" />
        <div aria-hidden="true" className="safe-bottom" />
      </main>

      {/* Hidden rather than unmounted. Playback would survive either way — the
          <audio> element lives in the engine, outside React — but PlayerBar
          holds local state such as an open queue sheet, and remounting it every
          time the keyboard appears would drop that and flicker. The spacers
          above stay reserved too, so the list does not lurch as the keyboard
          opens and closes under your thumb.

          md:visible because a desktop has no on-screen keyboard to hide from,
          and the sidebar layout keeps the bar clear of everything anyway. */}
      <div
        className={cn(
          'safe-bottom fixed inset-x-0 bottom-0 z-30 border-t border-border bg-surface-raised md:left-60',
          keyboardOpen && 'invisible md:visible',
        )}
      >
        <IncognitoBanner />
        <PlayerBar />
        <TabBar />
      </div>

      {/* The status bar strip. Sticky toolbars park below it via `top-safe`,
          which leaves the band above them showing whatever is scrolling past.
          This paints it in the page background so content disappears behind the
          clock rather than sliding across it. Zero height everywhere the inset
          is zero, so it costs nothing on a desktop or a non-notched device. */}
      <div
        aria-hidden="true"
        className="h-safe-top pointer-events-none fixed inset-x-0 top-0 z-30 bg-surface-sunken"
      />

      <ShareSheet />
      <Toaster />
      <AutoplayCoordinator />
      <ReleaseAnnouncement />
    </div>
  )
}
